import React, { useState } from 'react';
import { X, BookOpen, Award, TrendingUp, Clock, Target, Download, CheckCircle, Lightbulb } from 'lucide-react';

interface Course {
  id: string;
  title: string;
  category: string;
  duration_hours: number;
  level: string;
  description: string;
  topics: string[];
  requirements: string;
  certificate: boolean;
  pup_points: number;
  online: boolean;
  price_pln: number;
  target_audience: string;
  outcomes: string[];
}

interface CourseRecommendation {
  course: Course;
  relevance_score: number;
  reason: string;
  priority: 'high' | 'medium' | 'low';
  expectedBenefit: string;
  timeToComplete: string;
}

interface CourseRecommendations {
  recommendations: CourseRecommendation[];
  totalPoints: number;
  totalHours: number;
  estimatedDuration: string;
  learningPath: string;
  summary: string;
}

interface Props {
  recommendations: CourseRecommendations | null;
  onClose: () => void;
  onGenerateCertificate?: (courseId: string) => void;
}

export const CourseSuggestionsModal: React.FC<Props> = ({ recommendations, onClose, onGenerateCertificate }) => {
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);

  if (!recommendations) return null;

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <span className="px-3 py-1 text-xs font-bold bg-red-100 text-red-700 rounded-full uppercase">Wysoki priorytet</span>;
      case 'medium':
        return <span className="px-3 py-1 text-xs font-bold bg-yellow-100 text-yellow-700 rounded-full uppercase">Średni priorytet</span>;
      case 'low':
        return <span className="px-3 py-1 text-xs font-bold bg-green-100 text-green-700 rounded-full uppercase">Niski priorytet</span>;
      default:
        return null;
    }
  };

  const getLevelBadge = (level: string) => {
    const colors: Record<string, string> = {
      'podstawowy': 'bg-blue-100 text-blue-700',
      'średnio-zaawansowany': 'bg-purple-100 text-purple-700',
      'zaawansowany': 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${colors[level] || 'bg-gray-100 text-gray-700'}`}>
        {level}
      </span>
    );
  };

  const getScoreColor = (score: number) => {
    if (score >= 9) return 'text-green-600';
    if (score >= 7) return 'text-yellow-600';
    return 'text-orange-600';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Rekomendowane Kursy Fundacji NH</h2>
              <p className="text-purple-100 text-sm mt-1">Ścieżka rozwoju dopasowana do Twojego biznesu</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary Overview */}
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 border-b">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <BookOpen className="w-4 h-4" />
                <span>Rekomendowanych kursów</span>
              </div>
              <div className="text-2xl font-bold text-purple-600">
                {recommendations.recommendations.length}
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <Award className="w-4 h-4" />
                <span>Łączne punkty PUP</span>
              </div>
              <div className="text-2xl font-bold text-green-600">
                +{recommendations.totalPoints}
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <Clock className="w-4 h-4" />
                <span>Łączny czas nauki</span>
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {recommendations.totalHours}h
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <Target className="w-4 h-4" />
                <span>Czas realizacji</span>
              </div>
              <div className="text-lg font-bold text-indigo-600">
                {recommendations.estimatedDuration}
              </div>
            </div>
          </div>

          {/* Summary Text */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              Podsumowanie
            </h3>
            <p className="text-gray-700 text-sm">{recommendations.summary}</p>
          </div>
        </div>

        {/* Courses List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {recommendations.recommendations.map((rec, index) => (
              <div
                key={rec.course.id}
                className="bg-white border-2 border-gray-200 rounded-lg shadow-sm hover:shadow-md transition"
              >
                {/* Course Header */}
                <div className="p-4 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-purple-600">#{index + 1}</span>
                        <h3 className="text-xl font-bold text-gray-900">{rec.course.title}</h3>
                        {getPriorityBadge(rec.priority)}
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
                          {rec.course.category}
                        </span>
                        {getLevelBadge(rec.course.level)}
                        <span className="flex items-center gap-1 text-gray-600">
                          <Clock className="w-4 h-4" />
                          {rec.course.duration_hours}h
                        </span>
                        {rec.course.pup_points > 0 && (
                          <span className="flex items-center gap-1 text-green-600 font-semibold">
                            <Award className="w-4 h-4" />
                            +{rec.course.pup_points} pkt PUP
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600 mb-1">Relevance Score</div>
                      <div className={`text-3xl font-bold ${getScoreColor(rec.relevance_score)}`}>
                        {rec.relevance_score}/10
                      </div>
                    </div>
                  </div>
                </div>

                {/* Course Body */}
                <div className="p-4">
                  {/* Description */}
                  <p className="text-gray-700 mb-4">{rec.course.description}</p>

                  {/* Reason for Recommendation */}
                  <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-3">
                    <div className="flex items-start gap-2">
                      <Target className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-blue-800 uppercase block mb-1">
                          Dlaczego ten kurs?
                        </span>
                        <p className="text-sm text-blue-900">{rec.reason}</p>
                      </div>
                    </div>
                  </div>

                  {/* Expected Benefit */}
                  <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-3">
                    <div className="flex items-start gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-semibold text-green-800 uppercase block mb-1">
                          Spodziewana korzyść
                        </span>
                        <p className="text-sm text-green-900">{rec.expectedBenefit}</p>
                      </div>
                    </div>
                  </div>

                  {/* Topics */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Program kursu:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {rec.course.topics.map((topic, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Outcomes */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Po ukończeniu kursu:</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      {rec.course.outcomes.map((outcome, i) => (
                        <li key={i}>{outcome}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Additional Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-semibold text-gray-700">Wymagania:</span>
                      <p className="text-gray-600">{rec.course.requirements}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Czas realizacji:</span>
                      <p className="text-gray-600">{rec.timeToComplete}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Certyfikat:</span>
                      <p className="text-gray-600">{rec.course.certificate ? '✓ Tak' : '✗ Nie'}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Cena:</span>
                      <p className="text-green-600 font-semibold">
                        {rec.course.price_pln === 0 ? 'DARMOWY' : `${rec.course.price_pln} zł`}
                      </p>
                    </div>
                  </div>

                  {/* Action Button */}
                  {onGenerateCertificate && (
                    <div className="mt-4 pt-4 border-t">
                      <button
                        onClick={() => onGenerateCertificate(rec.course.id)}
                        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition flex items-center justify-center gap-2 font-medium"
                      >
                        <Award className="w-5 h-5" />
                        Generuj Certyfikat
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Learning Path */}
          <div className="mt-6 bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg p-6">
            <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2 text-lg">
              <Target className="w-6 h-6" />
              Zalecana Ścieżka Rozwoju
            </h3>
            <p className="text-purple-900 whitespace-pre-line leading-relaxed">{recommendations.learningPath}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <strong>Fundacja Nowe Horyzonty</strong> • Wszystkie kursy są darmowe dla kandydatów PUP Kraków
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition font-medium"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
};
